import { supabase } from '../lib/supabase';

export type EntityType = 'skill' | 'effect' | 'entity';

export interface ImageUploadResult {
  success: boolean;
  url?: string;
  error?: string;
  publicUrl?: string;
}

export interface ImageMetadata {
  entityType: EntityType;
  entityId: string;
  fileName: string;
  fileSize?: number;
  mimeType?: string;
}

const BUCKET_NAME = 'raid-boss-icons';
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_MIME_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/gif'];

export class ImageUploadService {
  /**
   * 이미지 파일 업로드
   */
  static async uploadImage(
    file: File,
    entityType: EntityType,
    entityId: string
  ): Promise<ImageUploadResult> {
    try {
      // 파일 유효성 검사
      if (!file) {
        return { success: false, error: '파일이 선택되지 않았습니다.' };
      }

      if (file.size > MAX_FILE_SIZE) {
        return { success: false, error: '파일 크기는 5MB를 초과할 수 없습니다.' };
      }

      if (!ALLOWED_MIME_TYPES.includes(file.type)) {
        return { success: false, error: '허용되지 않는 파일 형식입니다. (PNG, JPG, WebP, GIF만 가능)' };
      }

      // 파일명 생성 (entity_type/entity_id/timestamp_filename)
      const timestamp = Date.now();
      const fileExt = file.name.split('.').pop();
      const fileName = `${entityType}/${entityId}/${timestamp}.${fileExt}`;

      // Storage에 업로드
      const { data, error } = await supabase.storage
        .from(BUCKET_NAME)
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        console.error('Upload error:', error);
        return { success: false, error: '이미지 업로드에 실패했습니다.' };
      }

      // 공개 URL 가져오기
      const { data: { publicUrl } } = supabase.storage
        .from(BUCKET_NAME)
        .getPublicUrl(fileName);

      // 메타데이터 저장
      await this.saveImageMetadata({
        entityType,
        entityId,
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type,
        storagePath: fileName,
      });

      // 해당 엔티티의 image_url 업데이트
      await this.updateEntityImageUrl(entityType, entityId, publicUrl);

      return {
        success: true,
        url: fileName,
        publicUrl
      };
    } catch (error) {
      console.error('Image upload error:', error);
      return {
        success: false,
        error: '이미지 업로드 중 오류가 발생했습니다.'
      };
    }
  }

  /**
   * 이미지 삭제
   */
  static async deleteImage(
    entityType: EntityType,
    entityId: string
  ): Promise<ImageUploadResult> {
    try {
      // 현재 이미지 정보 가져오기
      const metadata = await this.getImageMetadata(entityType, entityId);
      
      if (!metadata) {
        return { success: false, error: '삭제할 이미지를 찾을 수 없습니다.' };
      }

      // Storage에서 삭제
      const { error } = await supabase.storage
        .from(BUCKET_NAME)
        .remove([metadata.storage_path]);

      if (error) {
        console.error('Delete error:', error);
        return { success: false, error: '이미지 삭제에 실패했습니다.' };
      }

      // 메타데이터 삭제
      await supabase
        .from('image_metadata')
        .delete()
        .eq('entity_type', entityType)
        .eq('entity_id', entityId);

      // 엔티티의 image_url null로 업데이트
      await this.updateEntityImageUrl(entityType, entityId, null);

      return { success: true };
    } catch (error) {
      console.error('Image delete error:', error);
      return {
        success: false,
        error: '이미지 삭제 중 오류가 발생했습니다.'
      };
    }
  }

  /**
   * 이미지 메타데이터 저장
   */
  private static async saveImageMetadata(data: {
    entityType: EntityType;
    entityId: string;
    fileName: string;
    fileSize?: number;
    mimeType?: string;
    storagePath: string;
  }) {
    const { error } = await supabase
      .from('image_metadata')
      .upsert({
        entity_type: data.entityType,
        entity_id: data.entityId,
        file_name: data.fileName,
        file_size: data.fileSize,
        mime_type: data.mimeType,
        storage_path: data.storagePath,
      });

    if (error) {
      console.error('Metadata save error:', error);
    }
  }

  /**
   * 이미지 메타데이터 조회
   */
  private static async getImageMetadata(entityType: EntityType, entityId: string) {
    const { data, error } = await supabase
      .from('image_metadata')
      .select('*')
      .eq('entity_type', entityType)
      .eq('entity_id', entityId)
      .single();

    if (error) {
      console.error('Metadata fetch error:', error);
      return null;
    }

    return data;
  }

  /**
   * 엔티티의 image_url 업데이트
   */
  private static async updateEntityImageUrl(
    entityType: EntityType,
    entityId: string,
    imageUrl: string | null
  ) {
    let table: string;
    let idColumn: string;

    switch (entityType) {
      case 'skill':
        table = 'boss_skill_renewed';
        idColumn = 'skill_code';
        break;
      case 'effect':
        table = 'boss_effect_renewed';
        idColumn = 'effect_code';
        break;
      case 'entity':
        table = 'raid_entity_renewed';
        idColumn = 'entity_code';
        break;
    }

    const { error } = await supabase
      .from(table)
      .update({
        image_url: imageUrl,
        image_uploaded_at: imageUrl ? new Date().toISOString() : null
      })
      .eq(idColumn, entityId);

    if (error) {
      console.error('Entity update error:', error);
      throw error;
    }
  }

  /**
   * 이미지 URL 조회
   */
  static async getImageUrl(
    entityType: EntityType,
    entityId: string
  ): Promise<string | null> {
    let table: string;
    let idColumn: string;

    switch (entityType) {
      case 'skill':
        table = 'boss_skill_renewed';
        idColumn = 'skill_code';
        break;
      case 'effect':
        table = 'boss_effect_renewed';
        idColumn = 'effect_code';
        break;
      case 'entity':
        table = 'raid_entity_renewed';
        idColumn = 'entity_code';
        break;
    }

    const { data, error } = await supabase
      .from(table)
      .select('image_url')
      .eq(idColumn, entityId)
      .single();

    if (error || !data) {
      return null;
    }

    return data.image_url;
  }
}